import { CopyButton } from '@/components/ui/copy-button';
import { cn } from '@/lib/cn';
import { applyLatexFallbacks } from '@/lib/latex-fallback';
import { alignClass } from '@/lib/markdown-align';
import { normalizeCjkEmphasis } from '@/lib/markdown-cjk-emphasis';
import { normalizeMarkdownTables } from '@/lib/markdown-table-normalize';
import { isChartLang, isInlineRenderCodeChild, isMermaidLang } from '@/lib/mermaid-fence';
import { type ReactNode, isValidElement, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { MarkdownImg } from '../wiki/markdownImg';
import { transformWikilinks } from '../wiki/wikilink-utils';
import { InlineChart } from './InlineChart';
import { InlineMermaid } from './InlineMermaid';

/**
 * LLM 답변 또는 위키 자료 미리보기에 raw HTML (`<p style=...>` 등) 이 섞여 있어도 안전하게
 * 렌더. defaultSchema 가 위험 태그/속성을 차단하면서 `<table>` `<sub>` `<sup>` 등 본문에 흔한
 * 태그는 그대로 살린다. `style` 속성은 제거되어 변환기가 넣은 폰트/색상이 깔끔하게 사라짐.
 *
 * `<code class="language-chart">` 의 className 은 defaultSchema 의 code 화이트리스트에 포함돼
 * 유지되므로 InlineChart 처리도 그대로 동작.
 */
const sanitizeSchema = defaultSchema;
const REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, sanitizeSchema]] as never;

// LaTeX color (`$\color{red}{\text{...}}$`) 의 색 이름 → 테마 토큰 className.
// 미등록 색은 className 없이 기본 텍스트색으로 (다크모드 가독성 + color_token_convention 준수).
const LATEX_COLOR_CLASS: Record<string, string> = {
  red: 'text-destructive',
  green: 'text-success',
  blue: 'text-accent',
};
function latexColorClass(color: string | undefined): string | undefined {
  return color ? LATEX_COLOR_CLASS[color.toLowerCase()] : undefined;
}

/**
 * vLLM 의 reasoning 모델이 thinking 토큰을 본문으로 흘리는 케이스 정리.
 * <think>...</think>, <thinking>...</thinking>, <|thinking|>...<|/thinking|> 블록 제거 +
 * 결과가 단일 'thought' / 'thinking' 단어면 빈 문자열 (메시지 자체 hide).
 */
function stripReasoning(raw: string): string {
  if (!raw) return raw;
  let s = raw
    .replace(/<think(?:ing)?>[\s\S]*?<\/think(?:ing)?>/gi, '')
    .replace(/<\|thinking\|>[\s\S]*?<\|\/thinking\|>/gi, '')
    .replace(/<\|reasoning\|>[\s\S]*?<\|\/reasoning\|>/gi, '');
  // 닫히지 않은 <think> 가 streaming 중 일시적으로 남아도 본문 표시는 그 뒤만.
  s = s.replace(/<think(?:ing)?>[\s\S]*$/gi, '');
  const trimmed = s.trim();
  if (trimmed === 'thought' || trimmed === 'thinking') return '';
  return s;
}

interface Props {
  content: string;
  className?: string;
  /** 정의되어 있으면 답변 본문의 [[slug]] 위키링크 클릭 시 우측 미리보기 패널을 연다. */
  onWikilinkPeek?: (slug: string) => void;
  /** [[slug]] chip 의 표시 텍스트를 슬러그 대신 페이지 제목으로 치환하기 위한 lookup. */
  slugToTitle?: Map<string, string>;
  /** 정의되어 있으면 본문 이미지에 hover X 버튼 노출 — admin 의 자료 preview 에서 의미없는 figure 정리. */
  onImageDelete?: (filename: string) => void;
}

function buildComponents(
  onPeek: ((slug: string) => void) | undefined,
  slugToTitle: Map<string, string> | undefined,
  onImageDelete: ((filename: string) => void) | undefined,
): Components {
  // slugToTitle 에 있는 slug = 사용자에게 보이는 (권한·존재 통과) 페이지. 그 외는 끊긴 것으로
  // 간주해 transformWikilinks 가 해당 문장을 통째로 제거 (권한 없는 페이지 정보 누출 차단).
  const resolved = slugToTitle ? new Set(slugToTitle.keys()) : undefined;
  const xform = (children: ReactNode) =>
    transformWikilinks(children, { onPeek, slugToTitle, resolved });
  return {
    p: ({ children }) => <p className="my-3 first:mt-0 last:mb-0 leading-7">{xform(children)}</p>,
    h1: ({ children }) => (
      <h1 className="mt-6 mb-3 text-2xl font-semibold tracking-tight first:mt-0">
        {xform(children)}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="mt-6 mb-3 text-xl font-semibold tracking-tight first:mt-0">
        {xform(children)}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-5 mb-2 text-lg font-semibold tracking-tight first:mt-0">
        {xform(children)}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mt-4 mb-2 text-base font-semibold first:mt-0">{xform(children)}</h4>
    ),
    // strong/em/del 안에 [[slug]] 가 들어가도 chip 으로 변환되도록 xform 호출.
    // 예: **[[slug]]** 가 정상 strong 으로 닫혔을 때 children 은 string `[[slug]]` 임.
    strong: ({ children }) => (
      <strong className="font-semibold text-foreground">{xform(children)}</strong>
    ),
    em: ({ children }) => <em className="italic">{xform(children)}</em>,
    del: ({ children }) => <del>{xform(children)}</del>,
    // applyLatexFallbacks 가 `$\color{red}{\text{...}}$` 를 `<span color="red">...</span>` 로
    // 변환해 둔다. 색 이름을 다크모드에서도 읽히는 테마 토큰 className 으로 매핑 (그 외 색은 기본색).
    span: ({ children, ...rest }) => {
      const color = (rest as { color?: string }).color;
      return <span className={latexColorClass(color)}>{xform(children)}</span>;
    },
    ul: ({ children }) => <ul className="my-3 ml-5 list-disc space-y-1.5">{children}</ul>,
    ol: ({ children }) => <ol className="my-3 ml-5 list-decimal space-y-1.5">{children}</ol>,
    li: ({ children }) => <li className="leading-7 [&>p]:my-0">{xform(children)}</li>,
    blockquote: ({ children }) => (
      <blockquote className="my-3 border-l-2 border-border pl-4 text-foreground-muted">
        {children}
      </blockquote>
    ),
    hr: () => <hr className="my-5 border-border-muted" />,
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-accent underline underline-offset-2 hover:opacity-80"
      >
        {children}
      </a>
    ),
    code: ({ className, children, ...rest }) => {
      const isBlock = /language-/.test(className ?? '');
      if (isBlock) {
        if (isChartLang(className)) {
          return <InlineChart raw={String(children).replace(/\n$/, '')} />;
        }
        if (isMermaidLang(className)) {
          return <InlineMermaid raw={String(children).replace(/\n$/, '')} />;
        }
        return (
          <code className={cn('font-mono text-[13px]', className)} {...rest}>
            {children}
          </code>
        );
      }
      return (
        <code className="rounded-sm bg-code-bg px-1.5 py-0.5 font-mono text-[0.9em]" {...rest}>
          {children}
        </code>
      );
    },
    pre: ({ children }) => {
      if (isInlineRenderCodeChild(children)) {
        return <>{children}</>;
      }
      const codeText = extractCodeText(children).replace(/\n$/, '');
      return (
        <div className="group/code relative my-3">
          <pre className="overflow-x-auto rounded-md bg-code-bg p-3 font-mono text-[13px] leading-6">
            {children}
          </pre>
          <CopyButton
            text={codeText}
            label="코드 복사"
            className="absolute right-2 top-2 bg-code-bg opacity-0 group-hover/code:opacity-100 group-focus-within/code:opacity-100"
          />
        </div>
      );
    },
    table: ({ children }) => (
      <div className="my-3 overflow-x-auto">
        <table className="w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="border-b border-border">{children}</thead>,
    tr: ({ children }) => (
      <tr className="border-b border-border-muted last:border-0">{children}</tr>
    ),
    th: ({ children, node }) => (
      <th className={cn('px-3 py-2 font-medium text-foreground-muted', alignClass(node))}>
        {xform(children)}
      </th>
    ),
    td: ({ children, node }) => (
      <td className={cn('px-3 py-2 align-top', alignClass(node))}>{xform(children)}</td>
    ),
    img: ({ src, alt }) => (
      <MarkdownImg
        src={typeof src === 'string' ? src : undefined}
        alt={alt}
        onDelete={onImageDelete}
      />
    ),
  };
}

/** `pre` 의 자식 (`<code>` 엘리먼트) 에서 복사용 raw 텍스트를 재귀로 추출. */
function extractCodeText(children: ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractCodeText).join('');
  if (isValidElement(children)) {
    const props = (children as { props?: { children?: ReactNode } }).props;
    return extractCodeText(props?.children);
  }
  return '';
}

export function Markdown({
  content,
  className,
  onWikilinkPeek,
  slugToTitle,
  onImageDelete,
}: Props) {
  const rendered = normalizeMarkdownTables(
    normalizeCjkEmphasis(applyLatexFallbacks(stripReasoning(content))),
  );
  const components = useMemo(
    () => buildComponents(onWikilinkPeek, slugToTitle, onImageDelete),
    [onWikilinkPeek, slugToTitle, onImageDelete],
  );
  return (
    <div className={cn('text-base', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={REHYPE_PLUGINS}
        components={components}
      >
        {rendered}
      </ReactMarkdown>
    </div>
  );
}
